'use client';

import { useEffect, useMemo, useState } from 'react';

import Image from 'next/image';

interface FaviconImageProps {
  website: string;
  name: string;
  width: number;
  height: number;
  className?: string;
}

export function getFaviconDomain(website: string): string | null {
  const value = website.trim();

  if (!value) return null;

  try {
    const url = new URL(
      value.startsWith('//')
        ? `https:${value}`
        : /^[a-z][a-z\d+.-]*:\/\//i.test(value)
          ? value
          : `https://${value}`
    );

    return url.hostname.toLowerCase().replace(/\.$/, '') || null;
  } catch {
    return null;
  }
}

export default function FaviconImage({
  website,
  name,
  width,
  height,
  className = '',
}: FaviconImageProps) {
  const domain = getFaviconDomain(website);
  const sources = useMemo(() => {
    if (!domain) return [];

    const encodedDomain = encodeURIComponent(domain);

    return [
      `https://favicon.im/${encodedDomain}?larger=true&throw-error-on-404=true`,
      `https://www.google.com/s2/favicons?domain=${encodedDomain}&sz=${Math.max(width, height) * 2}`,
    ];
  }, [domain, height, width]);
  const [sourceIndex, setSourceIndex] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    setSourceIndex(0);
    setHasLoaded(false);
  }, [domain]);

  useEffect(() => {
    if (sourceIndex !== 0 || hasLoaded || sources.length < 2) return;

    // A provider outage can leave an image request pending without firing onError.
    // Move to the fallback quickly so a favicon never blocks the visible result.
    const fallbackTimer = window.setTimeout(() => {
      setSourceIndex((current) => current === 0 ? 1 : current);
    }, 2500);

    return () => window.clearTimeout(fallbackTimer);
  }, [hasLoaded, sourceIndex, sources.length]);

  const alt = `${name} favicon`;
  const source = sources[sourceIndex];

  if (!source) {
    return (
      <span
        role="img"
        aria-label={alt}
        className={`inline-flex shrink-0 items-center justify-center bg-gray-100 font-semibold text-gray-500 ${className}`}
        style={{ width, height }}
      >
        {name.trim().charAt(0).toUpperCase() || '?'}
      </span>
    );
  }

  return (
    <Image
      key={source}
      src={source}
      alt={alt}
      width={width}
      height={height}
      className={className}
      loading="lazy"
      unoptimized
      onLoad={() => setHasLoaded(true)}
      onError={() => {
        setHasLoaded(false);
        setSourceIndex((current) => current + 1);
      }}
    />
  );
}
